import { Semaphore } from "async-mutex";

/**
 * Semaphore for limiting the number of concurrent requests.
 * We added this when we had many concurrent requests (to the metrics endpoint)
 * that were timing out.
 */
export const requestsSemaphore = new Semaphore(5);

const throwAbortError = (signal: AbortSignal): never => {
  throw signal.reason ?? new DOMException("Aborted", "AbortError");
};

/**
 * Runs fn under semaphore, respecting AbortSignal.
 * Aborted waiters release their slot if acquire completes after abort.
 */
export const runWithSignal = async <T>(
  semaphore: Semaphore,
  signal: AbortSignal | undefined,
  fn: () => Promise<T>
): Promise<T> => {
  if (!signal) {
    return semaphore.runExclusive(fn);
  }

  if (signal.aborted) {
    throwAbortError(signal);
  }

  const acquireTask = semaphore.acquire();

  const releaseIfAcquiredLater = () => {
    void acquireTask.then(([, release]) => release()).catch(() => {});
  };

  let release: (() => void) | undefined;

  try {
    const [, releaser] = await new Promise<[number, () => void]>(
      (resolve, reject) => {
        const onAbort = () => {
          cleanup();
          releaseIfAcquiredLater();
          reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
        };

        const cleanup = () => signal.removeEventListener("abort", onAbort);

        signal.addEventListener("abort", onAbort);

        void acquireTask.then(
          (result) => {
            cleanup();
            resolve(result);
          },
          (err) => {
            cleanup();
            reject(err);
          }
        );
      }
    );

    release = releaser;

    if (signal.aborted) {
      release();
      release = undefined;
      throwAbortError(signal);
    }

    return await fn();
  } finally {
    release?.();
  }
};
